import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const PAGE_ID = "main_page";

const FALLBACK_CONTENT = `
<p>Сейчас на FishMap уже собрано {{spots_count}} рыболовных точек, {{comments_count}} отчётов от пользователей и {{places_count}} водоёмов в базе. Данные пополняются сообществом и помогают выбирать место не вслепую, а по реальному опыту рыбаков.</p>
<p>Используйте карту, фильтры, фотографии, отчёты и инструменты распознавания, чтобы быстрее подготовиться к выезду и найти подходящее место для рыбалки.</p>
`;

const STAT_CARDS = [
    { key: "spots_count",    label: "Рыболовных точек",  hint: "Точки на карте"     },
    { key: "comments_count", label: "Отчётов",      hint: "Отчёты рыбаков"     },
    { key: "places_count",   label: "Водоёмов",          hint: "Озёра, реки, пруды" },
    { key: "fish_count",     label: "Видов рыб",         hint: "Справочник"         },
    { key: "articles_count", label: "Материалов",        hint: "Статьи блога"       },
];

const FEATURES = [
    {
        title: "Интерактивная карта",
        text:  "Находите точки ловли, смотрите условия на месте, глубины и подходы к воде.",
        tint:  "rgba(56, 189, 248, 0.28)",   // sky blue
        tag:   "Карта",
    },
    {
        title: "Опыт сообщества",
        text:  "Оставляйте отчёты о рыбалке с фото улова, снасти и условий водоёма.",
        tint:  "rgba(52, 211, 153, 0.26)",   // emerald
        tag:   "Сообщество",
    },
    {
        title: "Распознавание рыбы по фото",
        text:  "Загрузите снимок — система выделит рыбу и покажет вероятные виды.",
        tint:  "rgba(167, 139, 250, 0.26)",  // violet
        tag:   "AI",
    },
    {
        title: "Подготовка к выезду",
        text:  "Изучайте виды рыб, блог и инструменты, чтобы выбирать место осознанно.",
        tint:  "rgba(249, 168, 212, 0.24)",  // pink
        tag:   "Инструменты",
    },
];

const PLACEHOLDER_KEYS = new Set(STAT_CARDS.map((s) => s.key));

function formatCounter(v) {
    if (typeof v !== "number" || !Number.isFinite(v)) return "—";
    return new Intl.NumberFormat("ru-RU").format(v);
}

function normalizeStats(data) {
    const ph = data?.placeholders && typeof data.placeholders === "object" ? data.placeholders : {};
    return STAT_CARDS.reduce((acc, s) => {
        const raw = ph[s.key];
        acc[s.key] = Number.isFinite(Number(raw)) ? Number(raw) : null;
        return acc;
    }, {});
}

function replaceStatPlaceholders(content, stats) {
    return String(content || "").replace(/{{\s*([a-z_]+)\s*}}/g, (match, key) => {
        if (!PLACEHOLDER_KEYS.has(key)) return match;
        return formatCounter(stats?.[key]);
    });
}

export default function Home() {
    const { isAuthenticated, ready } = useAuth();
    const [page,         setPage]         = useState(null);
    const [stats,        setStats]        = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_BASE_URL}/api/pages/${PAGE_ID}`, { skipAuth: true })
            .then((r)  => { if (!cancelled) setPage(r.data || null); })
            .catch(()  => { if (!cancelled) setPage(null); });

        setStatsLoading(true);
        axios.get(`${API_BASE_URL}/api/home/stats`, { skipAuth: true })
            .then((r)  => { if (!cancelled) setStats(normalizeStats(r.data)); })
            .catch(()  => { if (!cancelled) setStats(null); })
            .finally(() => { if (!cancelled) setStatsLoading(false); });

        return () => { cancelled = true; };
    }, []);

    const title    = "Добро пожаловать в FishMap";
    const content         = page?.content         || FALLBACK_CONTENT;
    const renderedContent = useMemo(() => replaceStatPlaceholders(content, stats), [content, stats]);

    function openLoginModal(event, nextPath) {
        if (!ready || isAuthenticated) return;
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("auth:open-login-modal", {
            detail: { nextPath, title: "Необходима авторизация" },
        }));
    }

    return (
        <>
            <div className="home-page">
                <div aria-hidden className="home-page-background">
                    <div className="home-page-gradient-1"/>
                    <div className="home-page-gradient-2"/>
                    <div className="home-page-gradient-3"/>
                    <div className="home-page-gradient-4"/>
                </div>

                <section className="home-page-head">
                    <div className="home-page-head-inner">

                        <p className="home-page-head-pre-header">
                            Карта · Сообщество · Инструменты
                        </p>

                        <h1 className="home-page-head-title">
                            {title}
                        </h1>

                        <p className="home-page-head-text">
                            Находите проверенные точки на карте, изучайте водоёмы и делитесь опытом
                        </p>

                        <div className="ready-buttons" style={{ marginBottom:"52px" }}>
                            <Link to="/map"                  className="btn-primary-linear-gradient">Открыть карту</Link>
                            <Link to="/tools/fish-detection" className="btn-ghost-linear-gradient">Определить рыбу по фото</Link>
                        </div>

                        <div className="scroll-arrow">↓</div>
                    </div>
                </section>

                <section className="home-section">
                    <div className="home-section-inner">
                        <div className="deep-linear-gradient sweep-linear-gradient">
                            <div className="statistics-grid">
                                {STAT_CARDS.map((s) => (
                                    <div key={s.key} className="statistics-cell">
                                        <div className="statistics-counter">
                                            {statsLoading ? "—" : formatCounter(stats?.[s.key])}
                                        </div>
                                        <div className="statistics-label">
                                            {s.label}
                                        </div>
                                        <div className="statistics-hint">
                                            {s.hint}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="home-section">
                    <div className="home-section-inner">
                        <div className="features-header-div">
                            <h2 className="features-header">Всё важное перед выездом</h2>
                            <p className="features-header-text">
                                Современные инструменты для подготовки к рыбалке
                            </p>
                        </div>

                        <div className="features-grid">
                            {FEATURES.map((f) => (
                                <article
                                    key={f.title}
                                    className="feature-card linear-gradient"
                                    style={{ background: `linear-gradient(145deg, rgba(255,255,255,0.18) 0%, ${f.tint} 100%)` }}
                                >
                                    <div className="features-card-inner">
                                        <h3>{f.title}</h3>
                                    </div>
                                    <p>{f.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {renderedContent && (
                    <section className="home-section">
                        <div className="home-section-inner">
                            <div className="deep-linear-gradient rendered-content-div">
                                <div className="rich-content rendered-content-data" dangerouslySetInnerHTML={{ __html: renderedContent }} />
                            </div>
                        </div>
                    </section>
                )}

                <section style={{ position:"relative", zIndex:1, padding:"0 20px 120px" }}>
                    <div style={{ maxWidth:"1200px", margin:"0 auto" }}>
                        <div className="deep-linear-gradient cta-glow">
                            <h2>Готовы найти идеальное место?</h2>
                            <p>Присоединяйтесь к сообществу рыбаков</p>
                            <div className="ready-buttons">
                                <Link to="/map" className="btn-primary-linear-gradient">Начать сейчас</Link>
                                <Link to="/spots/new" className="btn-ghost-linear-gradient" onClick={(e) => openLoginModal(e, "/spots/new")}>
                                    Добавить точку
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
